"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import {
  BeerPanel,
  ConvertPanel,
} from "@/components/tools/chem-physical/beer-panels";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";

/** English strings. A translated content file overrides these through `ui`. */
export const BEER_LAMBERT_LAW_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to calculate?",
  modeBeer: "A = εlc for any term",
  modeConvert: "Absorbance ↔ transmittance",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  solveLabel: "Solve for",
  results: "Results",
  working: "The working",
  answerSuffix: "{label} (answer)",
  tryLabel: "Try an example",
  exampleNadh: "NADH at 340 nm, 100 µM",
  exampleDye: "A dye with ε = 25 000, 20 µM",
  termA: "Absorbance (A)",
  termE: "Molar absorption coefficient (ε)",
  termL: "Path length (l)",
  termC: "Concentration (c)",
  percentT: "Transmittance (%T)",
  transmittanceFraction: "Transmittance (T)",
  absorbed: "Light absorbed",
  unitSignal: "Reading shown as",
  unitCoefficient: "Coefficient unit",
  unitPath: "Path length unit",
  unitConcentration: "Concentration unit",
  coefficientHint:
    "ε at the wavelength you measure. M⁻¹ cm⁻¹ is the same as L mol⁻¹ cm⁻¹; the SI unit m² mol⁻¹ is 10 times smaller in number.",
  rangeLow:
    "Absorbance below about 0.1 is close to the instrument's noise. Use a more concentrated sample or a longer path if you can.",
  rangeHigh:
    "Absorbance above about 1 is outside the range where most spectrophotometers are accurate, because stray light bends the line. Dilute the sample and measure again.",
  equation: "A = ε × l × c",
  rearrangedE: "ε = A ÷ (l × c)",
  rearrangedL: "l = A ÷ (ε × c)",
  rearrangedC: "c = A ÷ (ε × l)",
  convertLine: "{label}: {value} {unit} = {converted}",
  convertSignal: "Reading: {value} {unit} → A = {a}",
  substitutedA: "A = {e} × {l} × {c} = {result}",
  substitutedE: "ε = {a} ÷ ({l} × {c}) = {result} M⁻¹ cm⁻¹",
  substitutedL: "l = {a} ÷ ({e} × {c}) = {result} cm",
  substitutedC: "c = {a} ÷ ({e} × {l}) = {result} mol/L",
  answerLine: "{result} = {answer}",
  transmittanceLine: "%T = 10^(2 − A) = 10^(2 − {a}) = {pct}%",
  baseUnits: { e: "M⁻¹ cm⁻¹", l: "cm", c: "mol/L" },
  convertInput: "Reading to convert",
  convertEquation: "A = −log₁₀ T = 2 − log₁₀(%T)",
  convertWorking: "A = {a};  T = 10^(−A) = {t};  %T = {pct}",
  enterValues: "Enter the remaining values to see the answer.",
  errorUnreadable:
    "{label} is not a number. Use digits, such as 2.50 or 1.5e3.",
  errorNonPositive: "{label} must be greater than zero.",
  errorNegativeAbsorbance:
    "Absorbance cannot be negative. A reading just below zero means the blank was a little darker than the sample; re-blank and measure again.",
  errorTransmittanceRange:
    "Transmittance must be above 0 and no more than 100% (or 1 as a fraction): a sample cannot transmit more light than it receives.",
  errorZeroAbsorbance:
    "Absorbance is zero, so the sample absorbs nothing at this wavelength and ε, l or c cannot be found from it.",
  copy: "Copy answer",
  copied: "Copied!",
  copyText: "{label}: {value}",
} as const;

type Mode = "beer" | "convert";
const SIG_CHOICES = ["3", "4", "5", "6"] as const;

export default function BeerLambertLawCalculatorWidget() {
  const strings = useToolUi(BEER_LAMBERT_LAW_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("beer");
  const [sigChoice, setSigChoice] = useState("match");
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="bl-mode"
        onChange={setMode}
        options={[
          { value: "beer", label: strings.modeBeer },
          { value: "convert", label: strings.modeConvert },
        ]}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="bl-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
      </div>
      {mode === "beer" ? (
        <BeerPanel sigChoice={sigChoice} strings={strings} />
      ) : (
        <ConvertPanel sigChoice={sigChoice} strings={strings} />
      )}
    </>
  );
}
